import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { formatDate, formatCurrency } from '../utils/dateUtils';
import PaginatedTable from './common/PaginatedTable';
import '../styles/ContractDetails.css';

// Helper function to convert points to hours
const convertToHours = (points, dollarPerHour) => {
  if (!points || !dollarPerHour || dollarPerHour === 0) return 0;
  return ((parseFloat(points) / parseFloat(dollarPerHour)) * 100).toFixed(2);
};

const ClientFinancials = () => {
  const { contractId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [financialDetails, setFinancialDetails] = useState({
    invoices: [],
    payments: [],
    credits: []
  });

  // Contract data state
  const [contract, setContract] = useState(null);
  const [contractLoading, setContractLoading] = useState(true);
  const [contractError, setContractError] = useState(null);

  useEffect(() => {
    fetchFinancialData();
  }, [contractId]);

  // Fetch contract details for display type and dollar per hour
  useEffect(() => {
    setContractLoading(true);
    setContractError(null);
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';
    fetch(`${apiBaseUrl}/api/contracts/${contractId}`)
      .then(res => res.json())
      .then(data => {
        console.log('Contract data for financial conversion:', data);
        setContract(data);
      })
      .catch(err => {
        console.error('Failed to load contract details:', err);
        setContractError('Failed to load contract details');
      })
      .finally(() => setContractLoading(false));
  }, [contractId]);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiBaseUrl = process.env.REACT_APP_API_URL || '';

      // Fetch performance data for financial summary
      const performanceResponse = await fetch(`${apiBaseUrl}/api/client-dashboard/performance/${contractId}`);
      if (!performanceResponse.ok) {
        throw new Error('Failed to fetch performance data');
      }
      const performanceData = await performanceResponse.json();
      setPerformanceData(performanceData);

      // Fetch financial details for transactions
      const financialsResponse = await fetch(`${apiBaseUrl}/api/contracts/${contractId}/financials`);
      if (!financialsResponse.ok) {
        throw new Error('Failed to fetch financial details');
      }
      const financialsData = await financialsResponse.json();
      setFinancialDetails(financialsData);

    } catch (err) {
      console.error('Error fetching financial data:', err);
      setError('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  // Table headers and row renderers from ContractDetails.js
  const invoicesTableHead = (
    <TableRow>
      <TableCell>Invoice #</TableCell>
      <TableCell>Date</TableCell>
      <TableCell>Due Date</TableCell>
      <TableCell>Amount</TableCell>
      <TableCell>Balance</TableCell>
      <TableCell>{contract?.customer_display_type === 'hours' ? 'Hours' : 'Points'}</TableCell>
      <TableCell>View</TableCell>
    </TableRow>
  );

  const renderInvoiceRow = (invoice, index) => (
    <TableRow key={invoice.id || `invoice-${index}`}>
      <TableCell>{invoice.doc_number || 'N/A'}</TableCell>
      <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
      <TableCell>{formatDate(invoice.due_date)}</TableCell>
      <TableCell>{formatCurrency(invoice.total_amount || 0)}</TableCell>
      <TableCell>{formatCurrency(invoice.balance || 0)}</TableCell>
      <TableCell>
        {invoice.points 
          ? (contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
              ? convertToHours(invoice.points, contract.dollar_per_hour)
              : invoice.points)
          : '-'}
      </TableCell>
      <TableCell>
        {invoice.quickbooks_id ? (
          <a 
            href={`${process.env.REACT_APP_API_URL || ''}/api/quickbooks-pdf/invoices/${invoice.quickbooks_id}`}
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#6200ee', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
          >
            <PdfIcon fontSize="small" />
          </a>
        ) : '-'}
      </TableCell>
    </TableRow>
  );

  const creditsTableHead = (
    <TableRow>
      <TableCell>Credit #</TableCell>
      <TableCell>Date</TableCell>
      <TableCell>Amount</TableCell>
      <TableCell>Description</TableCell>
      <TableCell>{contract?.customer_display_type === 'hours' ? 'Hours' : 'Points'}</TableCell>
    </TableRow>
  );

  const renderCreditRow = (credit, index) => (
    <TableRow key={credit.id || `credit-${index}`}>
      <TableCell>{credit.doc_number || 'N/A'}</TableCell>
      <TableCell>{formatDate(credit.credit_date)}</TableCell>
      <TableCell>{formatCurrency(credit.total_amount || 0)}</TableCell>
      <TableCell>
        {credit.raw_data && 
         credit.raw_data.Line && 
         credit.raw_data.Line.some(line => line.Description) 
          ? credit.raw_data.Line.find(line => line.Description)?.Description 
          : credit.memo_data || '-'}
      </TableCell>
      <TableCell>
        {credit.points 
          ? (contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
              ? convertToHours(credit.points, contract.dollar_per_hour)
              : credit.points)
          : '-'}
      </TableCell>
    </TableRow>
  );

  const paymentsTableHead = (
    <TableRow>
      <TableCell>Payment #</TableCell>
      <TableCell>Date</TableCell>
      <TableCell>Amount</TableCell>
    </TableRow>
  );

  const renderPaymentRow = (payment, index) => (
    <TableRow key={payment.id || `payment-${index}`}>
      <TableCell>{payment.quickbooks_id || 'N/A'}</TableCell>
      <TableCell>{formatDate(payment.payment_date)}</TableCell>
      <TableCell>{formatCurrency(payment.total_amount || 0)}</TableCell>
    </TableRow>
  );

  return (
    <>
      {/* Loading State */}
      {loading && <LinearProgress style={{ marginBottom: '20px' }} />}

      {/* Error State */}
      {error && (
        <Card style={{ padding: '20px', textAlign: 'center', marginBottom: '20px' }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}

      {/* Financial Summary */}
      {!loading && !error && (
        <Card className="details-card" style={{ marginBottom: '20px' }}>
          <CardHeader title={`Financial Summary ${contract?.customer_display_type === 'hours' ? '(Hours)' : '(Points)'}`} />
          <Divider />
          <CardContent>
            <Box className="stats-grid five-cards" sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 3, mt: 2 }}>
              <div className="stat-card" style={{ height: 110 }}>
                <div className="stat-value">
                  {contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
                    ? convertToHours(performanceData.points_purchased || 0, contract.dollar_per_hour)
                    : Math.round(performanceData.points_purchased || 0)}
                </div>
                <div className="stat-label">
                  {contract?.customer_display_type === 'hours' ? 'Hours Purchased' : 'Points Purchased'}
                </div>
              </div>
              <div className="stat-card" style={{ height: 110 }}>
                <div className="stat-value">
                  {contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
                    ? convertToHours(performanceData.points_credited || 0, contract.dollar_per_hour)
                    : Math.round(performanceData.points_credited || 0)}
                </div>
                <div className="stat-label">
                  {contract?.customer_display_type === 'hours' ? 'Hours Credited' : 'Points Credited'}
                </div>
              </div>
              <div className="stat-card" style={{ height: 110 }}>
                <div className="stat-value">
                  {contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
                    ? convertToHours(performanceData.points_delivered || 0, contract.dollar_per_hour)
                    : Math.round(performanceData.points_delivered || 0)}
                </div>
                <div className="stat-label">
                  {contract?.customer_display_type === 'hours' ? 'Hours Delivered' : 'Points Delivered'}
                </div>
              </div>
              <div className="stat-card" style={{ height: 110 }}>
                <div className="stat-value">
                  {contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
                    ? convertToHours(performanceData.points_working || 0, contract.dollar_per_hour)
                    : Math.round(performanceData.points_working || 0)}
                </div>
                <div className="stat-label">
                  {contract?.customer_display_type === 'hours' ? 'Hours in Working' : 'Points in Working'}
                </div>
              </div>
              <div className="stat-card" style={{ height: 110 }}>
                <div className="stat-value">
                  {contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
                    ? convertToHours(performanceData.points_balance_including_working || 0, contract.dollar_per_hour)
                    : Math.round(performanceData.points_balance_including_working || 0)}
                </div>
                <div className="stat-label">
                  {contract?.customer_display_type === 'hours' ? 'Hours Balance' : 'Points Balance'}
                </div>
              </div>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Financial Transactions */}
      {!loading && !error && (
        <Card className="details-card">
          <CardHeader title="Financial Transactions" />
          <Divider />
          <CardContent>
            <Typography variant="h6" gutterBottom>Invoices</Typography>
            <PaginatedTable 
              data={financialDetails.invoices || []}
              tableHead={invoicesTableHead}
              renderRow={renderInvoiceRow}
              rowsPerPage={10}
              emptyMessage="No invoices found"
            />

            <Typography variant="h6" gutterBottom style={{ marginTop: '32px' }}>Credits</Typography>
            <PaginatedTable 
              data={financialDetails.credits || []}
              tableHead={creditsTableHead}
              renderRow={renderCreditRow}
              rowsPerPage={10}
              emptyMessage="No credits found"
            />

            <Typography variant="h6" gutterBottom style={{ marginTop: '32px' }}>Payments</Typography>
            <PaginatedTable 
              data={financialDetails.payments || []}
              tableHead={paymentsTableHead}
              renderRow={renderPaymentRow}
              rowsPerPage={10}
              emptyMessage="No payments found"
            />
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ClientFinancials; 